import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Grid,
  Chip,
  TextField,
  MenuItem,
  Paper,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Tooltip,
  Checkbox,
  TableHead,
} from '@mui/material';
import TableLoader from '../components/TableLoader';
import {
  FiPlus,
  FiEdit2,
  FiTrash2,
  FiSearch,
  FiChevronLeft,
  FiChevronRight,
  FiX,
  FiMail,
} from 'react-icons/fi';
import { FaWhatsapp } from 'react-icons/fa';
import { campaignsAPI } from '../services/api';
import { colors } from '../theme/modernTheme';
import { getSortIcon } from '../utils/sortIcon';
import { useMultiSelect } from '../hooks/useMultiSelect';
import BulkActionToolbar from '../components/BulkActionToolbar';
import BulkDeleteDialog from '../components/BulkDeleteDialog';
import DeleteConfirmationDialog from '../components/DeleteConfirmationDialog';
import { showSuccess, showError } from '../utils/toast';
import { COMMUNICATION_CHANNEL } from '../utils/constants';
import { useAuth } from '../context/AuthContext';
import { adjustPageAfterDeletion } from '../utils/paginationUtils';

export default function Campaigns() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { hasModulePermission, MODULE_PERMISSIONS } = useAuth();

  // Channel permissions based on user's module permissions
  const canUseEmail = hasModulePermission(MODULE_PERMISSIONS.EMAIL_MARKETING);
  const canUseWhatsApp = hasModulePermission(MODULE_PERMISSIONS.WHATSAPP_MARKETING);

  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Temporary filter states (not applied until user clicks Apply)
  const [searchInput, setSearchInput] = useState('');
  const [statusInput, setStatusInput] = useState('');
  const [channelInput, setChannelInput] = useState('');

  // Applied filter states (used for API calls)
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [channelFilter, setChannelFilter] = useState('');

  const [page, setPage] = useState(0);
  const [rowsPerPage] = useState(50);
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('desc');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [campaignToDelete, setCampaignToDelete] = useState(null);
  // const [campaignStatistics, setCampaignStatistics] = useState({});

  // Multi-select state
  const [totalCount, setTotalCount] = useState(0);
  const multiSelect = useMultiSelect(campaigns, totalCount, 'id');
  const [bulkDeleteDialogOpen, setBulkDeleteDialogOpen] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);

  // Initialize filters from URL parameters on mountPhone 1 must be in international format with country code followed by exactly 10 digits (e.g., +919876543210)
  useEffect(() => {
    const channelParam = searchParams.get('channel');

    if (channelParam) {
      setChannelInput(channelParam);
      setChannelFilter(channelParam);
    }
  }, []);

  // Auto-select channel if only one is available
  useEffect(() => {
    if (canUseEmail && !canUseWhatsApp) {
      setChannelInput('email');
      setChannelFilter('email');
    } else if (!canUseEmail && canUseWhatsApp) {
      setChannelInput('whatsapp');
      setChannelFilter('whatsapp');
    }
  }, [canUseEmail, canUseWhatsApp]);

  useEffect(() => {
    loadCampaigns();
  }, [statusFilter, search, page, sortBy, sortOrder, channelFilter]);

  const loadCampaigns = async () => {
    setLoading(true);
    try {
      const params = {
        page: page + 1,
        limit: rowsPerPage,
        sortBy: sortBy,
        sortOrder: sortOrder,
      };

      if (statusFilter) params.status = statusFilter;
      if (search) params.search = search;
      if (channelFilter) params.channel = channelFilter;

      const response = await campaignsAPI.getAll(params);
      setCampaigns(response.data);
      setTotalCount(response.pagination?.total || response.data.length);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    navigate('/campaigns/new');
  };

  const handleEdit = (campaign) => {
    navigate(`/campaigns/${campaign.id}`);
  };
  const handleView = (campaign) => {
    navigate(`/campaigns/${campaign.id}`);
  };

  const handleDeleteClick = (campaign) => {
    setCampaignToDelete(campaign);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    setDeleting(true);
    try {
      await campaignsAPI.delete(campaignToDelete.id);
      multiSelect.removeFromSelection(campaignToDelete.id);
      setDeleteDialogOpen(false);
      setCampaignToDelete(null);
      loadCampaigns();
    } catch (error) {
      showError('Failed to delete campaign');
    } finally {
      setDeleting(false);
    }
  };

  const handlePrevPage = () => {
    if (page > 0) {
      setPage(page - 1);
    }
  };

  const handleNextPage = () => {
    const totalPages = Math.ceil(totalCount / rowsPerPage);
    if (page < totalPages - 1) {
      setPage(page + 1);
    }
  };

  const handleSort = (field) => {
    if (sortBy === field) {
      // Toggle sort order if same field
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      // Set new field with default descending order
      setSortBy(field);
      setSortOrder('desc');
    }
    setPage(0); // Reset to first page when sorting changes
  };

  const handleApplyFilters = () => {
    setSearch(searchInput);
    setStatusFilter(statusInput);
    setChannelFilter(channelInput);
    setPage(0); // Reset to first page when applying filters
    multiSelect.clearSelection(); // Clear selection when filters change
  };

  const handleClearFilters = () => {
    setSearchInput('');
    setStatusInput('');
    setSearch('');
    setStatusFilter('');

    // Preserve channel filter if only one channel is available
    if (canUseEmail && !canUseWhatsApp) {
      setChannelInput('email');
      setChannelFilter('email');
    } else if (!canUseEmail && canUseWhatsApp) {
      setChannelInput('whatsapp');
      setChannelFilter('whatsapp');
    } else {
      setChannelInput('');
      setChannelFilter('');
    }

    setPage(0); // Reset to first page when clearing filters
    multiSelect.clearSelection(); // Clear selection when filters cleared
  };

  const handleFilterKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleApplyFilters();
    }
  };

  // Check if filters have changed from applied state
  const hasFilterChanges =
    searchInput !== search || statusInput !== statusFilter || channelInput !== channelFilter;

  const handleBulkDelete = async () => {
    setBulkDeleting(true);
    try {
      const selectionData = multiSelect.getSelectionData();
      const deleteCount = multiSelect.selectedCount;

      await campaignsAPI.bulkDelete(selectionData);

      setBulkDeleteDialogOpen(false);
      multiSelect.clearSelection();

      // Adjust page if needed after bulk deletion
      const newTotalCount = totalCount - deleteCount;
      adjustPageAfterDeletion(page, newTotalCount, rowsPerPage, setPage);

      loadCampaigns();
      showSuccess(`Successfully deleted ${deleteCount} campaign(s)`);
    } catch (error) {
      showError(error?.error?.message || 'Failed to delete campaigns');
    } finally {
      setBulkDeleting(false);
    }
  };

  const filteredAndSortedCampaigns = React.useMemo(() => {
    // Data is already filtered, sorted, and paginated by the backend
    return campaigns;
  }, [campaigns]);

  // Backend handles pagination, so we use campaigns directly
  const paginatedCampaigns = campaigns;

  const totalPages = Math.ceil(totalCount / rowsPerPage);

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} mt={1}>
        <Typography
          variant="h4"
          sx={{
            fontSize: '1.5rem',
            fontWeight: 700,
            color: colors.text.primary,
          }}
        >
          Campaigns
        </Typography>
        <Button
          variant="contained"
          startIcon={<FiPlus />}
          onClick={handleAdd}
          sx={{
            borderRadius: '4px',
            backgroundColor: colors.primary.main,
            '&:hover': { backgroundColor: colors.primary.dark },
          }}
        >
          Create Campaign
        </Button>
      </Box>

      {/* Filters and Pagination */}
      <Paper
        sx={{
          p: 2,
          mb: 3,
          borderRadius: '4px',
          boxShadow: 'none',
          opacity: multiSelect.selectedCount > 0 ? 0.5 : 1,
          pointerEvents: multiSelect.selectedCount > 0 ? 'none' : 'auto',
        }}
      >
        <Grid container spacing={1.5} alignItems="center">
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search campaigns..."
              value={searchInput}
              onKeyPress={handleFilterKeyPress}
              disabled={multiSelect.selectedCount > 0}
              InputProps={{
                endAdornment: searchInput ? (
                  <IconButton
                    size="small"
                    onClick={() => {
                      setSearchInput('');
                      setSearch('');
                      setPage(0);
                      multiSelect.clearSelection();
                    }}
                    disabled={multiSelect.selectedCount > 0}
                    sx={{ padding: '4px' }}
                  >
                    <FiX size={16} style={{ color: colors.text.secondary }} />
                  </IconButton>
                ) : (
                  <FiSearch style={{ marginRight: 8, color: colors.text.secondary }} />
                ),
              }}
              onChange={(e) => setSearchInput(e.target.value)}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <TextField
              select
              fullWidth
              size="small"
              label="Status"
              value={statusInput}
              disabled={multiSelect.selectedCount > 0}
              SelectProps={{
                MenuProps: {
                  PaperProps: {
                    sx: {
                      borderRadius: '2px',
                    },
                  },
                  sx: {
                    '& .MuiMenu-list': {
                      padding: '0px',
                    },
                  },
                },
              }}
              onChange={(e) => setStatusInput(e.target.value)}
            >
              <MenuItem
                value=""
                sx={{
                  p: '2px 8px',
                  minHeight: '32px',
                  '&:hover': { borderRadius: 0 },
                  '&.Mui-selected': { borderRadius: 0 },
                }}
              >
                All Status
              </MenuItem>
              <MenuItem
                value="DRAFT"
                sx={{
                  p: '2px 8px',
                  minHeight: '32px',
                  '&:hover': { borderRadius: 0 },
                  '&.Mui-selected': { borderRadius: 0 },
                }}
              >
                Draft
              </MenuItem>
              <MenuItem
                value="ACTIVE"
                sx={{
                  p: '2px 8px',
                  minHeight: '32px',
                  '&:hover': { borderRadius: 0 },
                  '&.Mui-selected': { borderRadius: 0 },
                }}
              >
                Active
              </MenuItem>
              <MenuItem
                value="COMPLETED"
                sx={{
                  p: '2px 8px',
                  minHeight: '32px',
                  '&:hover': { borderRadius: 0 },
                  '&.Mui-selected': { borderRadius: 0 },
                }}
              >
                Completed
              </MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12} md={2}>
            <TextField
              select
              fullWidth
              size="small"
              label="Channel"
              value={channelInput}
              disabled={
                multiSelect.selectedCount > 0 ||
                (canUseEmail && !canUseWhatsApp) ||
                (!canUseEmail && canUseWhatsApp)
              }
              onChange={(e) => setChannelInput(e.target.value)}
            >
              <MenuItem value="" sx={{ p: '2px 8px', minHeight: '32px' }}>
                All Channels
              </MenuItem>
              {canUseEmail && (
                <MenuItem value="email" sx={{ p: '2px 8px', minHeight: '32px' }}>
                  Email
                </MenuItem>
              )}
              {canUseWhatsApp && (
                <MenuItem value="whatsapp" sx={{ p: '2px 8px', minHeight: '32px' }}>
                  WhatsApp
                </MenuItem>
              )}
            </TextField>
          </Grid>
          <Grid item xs={12} md={2}>
            <Box display="flex" alignItems="center" gap={1}>
              <Button
                size="small"
                variant="text"
                onClick={handleApplyFilters}
                disabled={!hasFilterChanges || multiSelect.selectedCount > 0}
                sx={{
                  backgroundColor: 'transparent',
                  color: colors.primary.main,
                  textTransform: 'none',
                  fontSize: '0.875rem',
                  fontWeight: 510,
                  p: '6px 12px',
                  minWidth: 'auto',
                  '&:disabled': {
                    color: colors.text.disabled,
                  },
                  '&:hover': {
                    backgroundColor: 'transparent',
                  },
                }}
              >
                Apply
              </Button>
              <Button
                size="small"
                variant="text"
                onClick={handleClearFilters}
                disabled={
                  multiSelect.selectedCount > 0 ||
                  // Disable if only channel filter is set and only one channel is available
                  ((canUseEmail && !canUseWhatsApp) || (!canUseEmail && canUseWhatsApp)
                    ? !searchInput && !statusInput
                    : !searchInput && !statusInput && !channelInput)
                }
                sx={{
                  backgroundColor: 'transparent',
                  color: colors.primary.main,
                  textTransform: 'none',
                  fontSize: '0.875rem',
                  fontWeight: 510,
                  p: '6px 12px',
                  minWidth: 'auto',
                  '&:disabled': {
                    color: colors.text.disabled,
                  },
                  '&:hover': {
                    backgroundColor: 'transparent',
                  },
                }}
              >
                Clear
              </Button>
            </Box>
          </Grid>
          <Grid item xs={12} md={3}>
            <Box display="flex" alignItems="center" justifyContent="flex-end" gap={1}>
              <IconButton
                size="small"
                onClick={handlePrevPage}
                disabled={page === 0 || multiSelect.selectedCount > 0}
                sx={{
                  '&:hover': {
                    backgroundColor: colors.action.hover,
                    cursor: 'pointer',
                  },
                  '&:disabled': {
                    opacity: 0.5,
                    cursor: 'not-allowed',
                  },
                }}
              >
                <FiChevronLeft />
              </IconButton>
              <Typography
                variant="body2"
                sx={{
                  color: colors.text.secondary,
                  minWidth: '80px',
                  textAlign: 'center',
                  fontSize: '0.85rem',
                }}
              >
                Page {page + 1} of {totalPages || 1}
              </Typography>
              <IconButton
                size="small"
                onClick={handleNextPage}
                disabled={page >= totalPages - 1 || multiSelect.selectedCount > 0}
                sx={{
                  '&:hover': {
                    backgroundColor: colors.action.hover,
                    cursor: 'pointer',
                  },
                  '&:disabled': {
                    opacity: 0.5,
                    cursor: 'not-allowed',
                  },
                }}
              >
                <FiChevronRight />
              </IconButton>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Bulk Action Toolbar */}
      <BulkActionToolbar
        selectedCount={multiSelect.selectedCount}
        onClearSelection={multiSelect.clearSelection}
        onDelete={() => setBulkDeleteDialogOpen(true)}
        canDelete={true}
        itemName="campaigns"
        selectAllPages={multiSelect.selectAllPages}
        totalCount={totalCount}
        onSelectAllPages={multiSelect.selectAllAcrossPages}
      />

      {loading ? (
        <TableLoader message="Loading campaigns..." />
      ) : filteredAndSortedCampaigns.length === 0 ? (
        <Box
          sx={{
            textAlign: 'center',
            py: 8,
            color: colors.text.secondary,
          }}
        >
          <Typography variant="body1">No campaigns found</Typography>
        </Box>
      ) : (
        <Box>
          <TableContainer
            component={Paper}
            sx={{
              borderRadius: '4px',
              boxShadow: 'none',
              border: `1px solid ${colors.border.main}`,
              overflow: 'auto',
            }}
          >
            <Table>
              <TableHead sx={{ borderBottom: '1px solid #E5E7EB' }}>
                <TableRow>
                  <TableCell
                    padding="checkbox"
                    rowSpan={2}
                    sx={{ width: '2%', py: 1, px: 1, borderBottom: 'none' }}
                  >
                    <Checkbox
                      size="medium"
                      indeterminate={multiSelect.isSomeCurrentPageSelected}
                      checked={multiSelect.isAllCurrentPageSelected}
                      onChange={multiSelect.toggleSelectAll}
                      sx={{
                        padding: '4px',
                        color: colors.text.secondary,
                        '& .MuiSvgIcon-root': { fontSize: 28 },
                        transform: 'scale(1.2)',
                        '&.Mui-checked': {
                          color: colors.primary.main,
                        },
                        '&.MuiCheckbox-indeterminate': {
                          color: colors.primary.main,
                        },
                      }}
                    />
                  </TableCell>
                  <TableCell
                    rowSpan={2}
                    sx={{
                      paddingLeft: 0,
                      width: '250px',
                      fontWeight: 600,
                      color:
                        multiSelect.selectedCount > 0 ? colors.text.disabled : colors.text.primary,
                      fontSize: '0.75rem',
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em',
                      cursor: multiSelect.selectedCount > 0 ? 'not-allowed' : 'pointer',
                      userSelect: 'none',
                      borderBottom: 'none',
                      pointerEvents: multiSelect.selectedCount > 0 ? 'none' : 'auto',
                    }}
                    onClick={() => handleSort('name')}
                  >
                    <Box display="flex" alignItems="center" gap={0.5}>
                      Campaign Name
                      {getSortIcon(sortBy, sortOrder, 'name')}
                    </Box>
                  </TableCell>
                  <TableCell
                    rowSpan={2}
                    sx={{
                      width: '300px',
                      fontWeight: 600,
                      color: colors.text.primary,
                      fontSize: '0.75rem',
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em',
                      borderBottom: 'none',
                      textAlign: 'center',
                    }}
                  >
                    Status
                  </TableCell>
                  <TableCell
                    rowSpan={2}
                    sx={{
                      width: '350px',
                      fontWeight: 600,
                      color:
                        multiSelect.selectedCount > 0 ? colors.text.disabled : colors.text.primary,
                      fontSize: '0.75rem',
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em',
                      cursor: multiSelect.selectedCount > 0 ? 'not-allowed' : 'pointer',
                      userSelect: 'none',
                      borderBottom: 'none',
                      pointerEvents: multiSelect.selectedCount > 0 ? 'none' : 'auto',
                    }}
                    onClick={() => handleSort('total_contacts')}
                  >
                    <Box display="flex" alignItems="center" justifyContent="center" gap={0.5}>
                      Contacts
                      {getSortIcon(sortBy, sortOrder, 'total_contacts')}
                    </Box>
                  </TableCell>
                  <TableCell
                    rowSpan={2}
                    sx={{
                      width: '350px',
                      fontWeight: 600,
                      color: colors.text.primary,
                      fontSize: '0.75rem',
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em',
                      textAlign: 'center',
                      borderBottom: 'none',
                    }}
                  >
                    Channel
                  </TableCell>
                  <TableCell
                    rowSpan={2}
                    sx={{
                      width: '100px',
                      fontWeight: 600,
                      color: colors.text.primary,
                      fontSize: '0.75rem',
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em',
                      borderBottom: 'none',
                    }}
                  >
                    {multiSelect.selectedCount > 0 ? '' : 'Actions'}
                  </TableCell>
                </TableRow>
                <TableRow></TableRow>
              </TableHead>
              <TableBody>
                {paginatedCampaigns.map((campaign, index) => (
                  <TableRow
                    key={campaign.id}
                    onClick={() => handleView(campaign)}
                    sx={{
                      cursor: 'pointer',
                      backgroundColor: multiSelect.isSelected(campaign)
                        ? colors.primary.main + '10'
                        : 'transparent',
                      '&:hover': {
                        backgroundColor: multiSelect.isSelected(campaign)
                          ? colors.primary.main + '20'
                          : colors.action.row,
                      },
                      '&:last-child td, &:last-child th': { border: 0 },
                      '&:not(:last-of-type)': {
                        borderBottom: `1px solid ${colors.border.main}`,
                      },
                    }}
                  >
                    <TableCell
                      padding="checkbox"
                      sx={{ py: 1.5, px: 1 }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <Checkbox
                        size="medium"
                        checked={multiSelect.isSelected(campaign)}
                        onChange={(e) => {
                          e.stopPropagation();
                          multiSelect.toggleSelect(campaign);
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                        }}
                        sx={{
                          padding: '4px',
                          '& .MuiSvgIcon-root': { fontSize: 28 },
                          transform: 'scale(1.2)',
                          color: colors.text.secondary,
                          '&.Mui-checked': {
                            color: colors.primary.main,
                          },
                        }}
                      />
                    </TableCell>
                    <TableCell sx={{ maxWidth: '200px', paddingLeft: 0 }}>
                      <Typography
                        variant="body2"
                        sx={{
                          color: colors.text.primary,
                          fontSize: '0.875rem',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        <Tooltip title={campaign.name} arrow placement="top">
                          <span>{campaign.name}</span>
                        </Tooltip>
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ width: '100px', textAlign: 'center' }}>
                      <Chip
                        label={campaign.status}
                        size="small"
                        sx={{
                          backgroundColor:
                            campaign?.status === 'COMPLETED'
                              ? `${colors.success.main}15`
                              : campaign?.status === 'ACTIVE'
                                ? `${colors.error.main}15`
                                : colors.action.hover,
                          color:
                            campaign?.status === 'COMPLETED'
                              ? colors.success.main
                              : campaign?.status === 'ACTIVE'
                                ? colors.error.main
                                : colors.text.secondary,
                          fontWeight: 501,
                          borderRadius: '4px',
                          fontSize: '12px !important',
                          textAlign: 'center',
                        }}
                      />
                    </TableCell>
                    <TableCell align="center" sx={{ width: '90px', py: 1, textAlign: 'center' }}>
                      <Typography
                        variant="body2"
                        sx={{
                          color: colors.text.primary,
                        }}
                      >
                        {campaign.total_contacts || 0}
                      </Typography>
                    </TableCell>

                    <TableCell sx={{ width: '100px', textAlign: 'center' }}>
                      <Box display="flex" gap={1} justifyContent="center" alignItems="center">
                        {campaign?.communication_channel === COMMUNICATION_CHANNEL.EMAIL && (
                          <Tooltip title="Email" arrow>
                            <Box sx={{ color: colors.primary.main }}>
                              <FiMail size={18} />
                            </Box>
                          </Tooltip>
                        )}
                        {campaign?.communication_channel === COMMUNICATION_CHANNEL.WHATSAPP && (
                          <Tooltip title="WhatsApp" arrow>
                            <Box sx={{ color: '#25D366' }}>
                              <FaWhatsapp size={18} />
                            </Box>
                          </Tooltip>
                        )}
                        {!campaign?.communication_channel && (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ fontSize: '0.7rem' }}
                          >
                            -
                          </Typography>
                        )}
                      </Box>
                    </TableCell>

                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <Box
                        sx={{
                          display: 'flex',
                          gap: 0.5,
                          alignItems: 'center',
                        }}
                      >
                        {multiSelect.selectedCount > 0 ? (
                          <Box sx={{ width: '64px' }} />
                        ) : (
                          <>
                            {campaign.status === 'DRAFT' ? (
                              <IconButton
                                size="small"
                                sx={{
                                  color: colors.text.primary,
                                  p: 1,
                                  '&:hover': {
                                    color: colors.primary.main,
                                    backgroundColor: 'transparent',
                                    width: 32,
                                    height: 32,
                                  },
                                }}
                                onClick={() => handleEdit(campaign)}
                                title="Edit"
                              >
                                <FiEdit2 size={16} />
                              </IconButton>
                            ) : (
                              <IconButton
                                size="small"
                                disableRipple
                                disableFocusRipple
                                onClick={() => handleView(campaign)}
                                sx={{
                                  width: 32,
                                  height: 32,
                                  '&:hover': {
                                    backgroundColor: 'transparent',
                                    cursor: 'pointer',
                                  },
                                }}
                              ></IconButton>
                            )}
                            <IconButton
                              size="small"
                              sx={{
                                color: colors.text.primary,
                                p: 1,
                                '&:hover': {
                                  backgroundColor: 'transparent',
                                  color: colors.error.main,
                                },
                              }}
                              onClick={() => handleDeleteClick(campaign)}
                              title="Delete"
                            >
                              <FiTrash2 size={16} />
                            </IconButton>
                          </>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Bottom Pagination */}
          {!loading && filteredAndSortedCampaigns.length > 0 && (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'flex-end',
                alignItems: 'center',
                mt: 2,
                gap: 1,
                opacity: multiSelect.selectedCount > 0 ? 0.5 : 1,
                pointerEvents: multiSelect.selectedCount > 0 ? 'none' : 'auto',
              }}
            >
              <IconButton
                size="small"
                onClick={handlePrevPage}
                disabled={page === 0 || multiSelect.selectedCount > 0}
                sx={{
                  '&:hover': {
                    backgroundColor: colors.action.hover,
                    cursor: 'pointer',
                  },
                  '&:disabled': {
                    opacity: 0.5,
                    cursor: 'not-allowed',
                  },
                }}
              >
                <FiChevronLeft />
              </IconButton>
              <Typography
                variant="body2"
                sx={{
                  color: colors.text.secondary,
                  minWidth: '80px',
                  textAlign: 'center',
                  fontSize: '0.85rem',
                }}
              >
                Page {page + 1} of {totalPages || 1}
              </Typography>
              <IconButton
                size="small"
                onClick={handleNextPage}
                disabled={page >= totalPages - 1 || multiSelect.selectedCount > 0}
                sx={{
                  '&:hover': {
                    backgroundColor: colors.action.hover,
                    cursor: 'pointer',
                  },
                  '&:disabled': {
                    opacity: 0.5,
                    cursor: 'not-allowed',
                  },
                }}
              >
                <FiChevronRight />
              </IconButton>
            </Box>
          )}
        </Box>
      )}

      <DeleteConfirmationDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={handleDeleteConfirm}
        itemName="campaign"
        deleting={deleting}
      />

      {/* Bulk Delete Dialog */}
      <BulkDeleteDialog
        open={bulkDeleteDialogOpen}
        onClose={() => setBulkDeleteDialogOpen(false)}
        onConfirm={handleBulkDelete}
        count={multiSelect.selectedCount}
        itemName="campaigns"
        deleting={bulkDeleting}
      />
    </Box>
  );
}
